import './ToggleButtons.scss';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_FieldLabel } from '../../design/labels/fieldLabel/S_FIeldLabel';
import { RowLayout } from '../../modules/common/layouts/rowLayout/RowLayout';

interface IToggleButtonsMultiSelectProps<T> {
  label?: string;
  className?: string;
  options: T[];
  values: T[];
  valueField: keyof T;
  labelField: keyof T;
  onSelectChange: (selected: T[]) => void;
}

export const ToggleButtonsMultiSelect = observer(<T,>(props: IToggleButtonsMultiSelectProps<T>): ReactElement => {
  const { t } = useTranslator();

  const handleClick = (selected: T): void => {
    const { valueField, values, onSelectChange } = props;

    const output = values.slice();
    const element = output.find((o) => o[valueField] === selected[valueField]);
    const index = element ? output.indexOf(element) : -1;
    if (index < 0) {
      output.push(selected);
    } else {
      output.splice(index, 1);
    }
    onSelectChange(output);
  };

  const handleSelectAll = (): void => {
    props.onSelectChange(props.options);
  };

  const handleDeselectAll = (): void => {
    props.onSelectChange([]);
  };

  const { valueField, labelField, className, options, values, label } = props;

  return (
    <div className={`toggle-buttons ${className}`}>
      {label && <S_FieldLabel>{label}</S_FieldLabel>}
      <RowLayout size='md'>
        <span
          className='toggle-buttons-action'
          onClick={handleSelectAll}
        >
          {t('select_all')}
        </span>
        <span
          className='toggle-buttons-action'
          onClick={handleDeselectAll}
        >
          {t('deselect_all')}
        </span>
      </RowLayout>
      <div className='toggles'>
        {options.map((o) => (
          <Button
            key={o[valueField] as string}
            className='toggle'
            onClick={(): void => {
              handleClick(o);
            }}
            variant={!values.some((x) => x[valueField] === o[valueField]) ? 'outlined' : 'solid'}
          >
            <>{o[labelField]}</>
          </Button>
        ))}
      </div>
    </div>
  );
});
